/**
 * DashboardPage - Figma "Dashboard desktop" (PoP Website wireframe, node 1758:1356).
 *
 * Cream-2 page; greeting top-left; left rail with action pills + dog profile
 * card; main white plan card with status/dog stat tiles, next-delivery tile,
 * and the plan-item recipe grid.
 */

import React, { useMemo } from "react";
import { Box, Stack, Typography, ButtonBase } from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

import type { Dog } from "../types";
import { QUIZ } from "@/theme/quizTokens";
import { caloriesFromGrams } from "@/domains/nutrition/FoodEnergy";
import { QuizButton } from "../components/shared/QuizButton";
import {
  buildVariantIndex,
  parseBundlePlan,
  parseLegacySubscription,
  isActiveSub,
  isBundleSub,
} from "../utils/recharge";

// Recipe-card border colours rotate through the design palette
const CARD_BORDER_COLORS = ["#9cdebf", "#e5d1f2", "#cced85", "#8ee5ff", "#ffadbf"];

// Design green-2 for the "Active" tile (quiz green is reserved for selections)
const STATUS_GREEN = "#c9f669";

function formatDogAge(dog: Dog): string {
  const years = dog.ageYears ?? 0;
  const months = dog.ageMonths ?? 0;
  if (years === 0 && months > 0) return `${months}mo`;
  if (months > 0) return `${years}yrs ${months}mo`;
  return `${years}yrs`;
}

// "Fri 3 July", pinned to UTC so the day never shifts with the viewer's timezone
function formatDeliveryDate(dateStr: string | null): string | null {
  if (!dateStr) return null;
  const date = new Date(dateStr.slice(0, 10) + "T00:00:00Z");
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

function daysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const delivery = new Date(dateStr.slice(0, 10) + "T00:00:00Z");
  if (Number.isNaN(delivery.getTime())) return null;
  const now = new Date();
  const todayUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.round((delivery.getTime() - todayUTC) / (1000 * 60 * 60 * 24));
  return diff >= 0 ? diff : null;
}

type PlanItem = {
  variantId: string;
  title: string;
  imageUrl: string | null;
  countLabel: string;
};

function StatTile({
  label,
  value,
  background,
}: {
  label: string;
  value: string;
  background: string;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        background,
        borderRadius: "8px",
        px: 2,
        py: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 0,
      }}
    >
      <Typography
        sx={{ fontFamily: QUIZ.font.body, fontSize: { xs: 18, md: 20 }, color: QUIZ.color.grey }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: QUIZ.font.display,
          fontSize: { xs: 31, md: 42 },
          color: QUIZ.color.ink,
          textTransform: "uppercase",
          lineHeight: 1.1,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function ActionPill({
  label,
  background,
  textColor,
  onClick,
}: {
  label: string;
  /** MUI responsive value, e.g. { xs: "#4a3026", md: "#8ee5ff" } */
  background: any;
  textColor?: any;
  onClick?: () => void;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: "100%",
        background,
        borderRadius: QUIZ.radius.pill,
        px: 2.5,
        py: { xs: 2.1, md: 2.5 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "filter 150ms ease",
        "&:hover": { filter: "brightness(0.96)" },
      }}
    >
      <Typography
        sx={{
          fontFamily: QUIZ.font.body,
          fontWeight: 700,
          fontSize: { xs: 17, md: 20 },
          color: textColor ?? QUIZ.color.grey,
          textAlign: "center",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}

function DogStatRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        background: QUIZ.color.cream,
        py: 1,
        px: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "1px",
      }}
    >
      <Typography sx={{ fontFamily: QUIZ.font.body, fontWeight: 500, fontSize: 13, color: QUIZ.color.grey }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: QUIZ.font.body,
          fontWeight: 500,
          fontSize: 20,
          color: QUIZ.color.grey,
          textAlign: "center",
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

// TODO: Dog photos — the Figma design shows a photo at the top of this card
// with a camera badge for uploading one. There's no photo field on dog data or
// upload backend yet; when that exists, reinstate the photo block here
// (photo area + camera ButtonBase, see node 1758:5693 in the wireframe).
function DogProfileCard({ dog, onClick }: { dog: Dog; onClick?: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        width: "100%",
        background: "#fff",
        borderRadius: "20px",
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        pt: 1,
      }}
    >
      {/* Name */}
      <Typography
        sx={{
          fontFamily: QUIZ.font.display,
          fontSize: 40,
          color: QUIZ.color.ink,
          textAlign: "center",
          py: 1.5,
          lineHeight: 1,
        }}
      >
        {dog.name}
      </Typography>

      {/* Stats */}
      <Stack spacing="2px" sx={{ pb: "2px" }}>
        <DogStatRow label="Breed" value={dog.breed || "-"} />
        <DogStatRow label="Age" value={formatDogAge(dog)} />
        <DogStatRow
          label="Daily calories"
          value={
            dog.dailyGrams != null
              ? `${caloriesFromGrams(dog.dailyGrams)} kcal`
              : dog.dailyCalories != null
              ? `${dog.dailyCalories} kcal`
              : "-"
          }
        />
        <DogStatRow
          label="Daily amount"
          value={dog.dailyGrams != null ? `${dog.dailyGrams}g` : "-"}
        />
      </Stack>
    </Box>
  );
}

function PlanItemCard({ item, borderColor }: { item: PlanItem; borderColor: string }) {
  return (
    <Box
      sx={{
        width: { xs: "100%", md: 210 },
        flexShrink: 0,
        background: QUIZ.color.cream2,
        border: `2px solid ${borderColor}`,
        borderRadius: "8px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ height: { xs: 68, md: 206 }, background: QUIZ.color.cream2 }}>
        {item.imageUrl && (
          <Box
            component="img"
            src={item.imageUrl}
            alt={item.title}
            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        )}
      </Box>
      <Box
        sx={{
          flex: 1,
          display: "grid",
          placeItems: "center",
          px: 1,
          py: { xs: 0.75, md: 1.25 },
          minHeight: { xs: 36, md: 48 },
        }}
      >
        <Typography
          sx={{
            fontFamily: QUIZ.font.display,
            fontSize: { xs: 11.5, md: 16 },
            lineHeight: 1.1,
            color: QUIZ.color.ink,
            textTransform: "uppercase",
            textAlign: "center",
          }}
        >
          {item.title}
        </Typography>
      </Box>
      <Box
        sx={{
          background: QUIZ.color.brown,
          py: { xs: 0.9, md: 1.25 },
          display: "grid",
          placeItems: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: QUIZ.font.display,
            fontSize: { xs: 11.5, md: 16 },
            lineHeight: 1,
            color: QUIZ.color.white,
            textTransform: "uppercase",
          }}
        >
          {item.countLabel}
        </Typography>
      </Box>
    </Box>
  );
}

export function DashboardPage({
  customerFirstName,
  dogs,
  nextDeliveryDate,
  hasSubs,
  activeSubsCount,
  products = [],
  subscriptions = [],
  pausedSubs = [],
  onResume,
  onGoSubscriptions,
  onGoDogs,
  onGoUpcoming,
}: {
  customerFirstName?: string | null;
  dogs: Dog[];
  nextDeliveryDate: string | null;
  hasSubs: boolean;
  activeSubsCount: number;
  products?: any[];
  subscriptions?: any[];
  /** Cancelled bundle subs shown behind a frosted "resume" overlay when nothing is active */
  pausedSubs?: any[];
  onResume?: () => void;
  onGoSubscriptions: () => void;
  onGoDogs: () => void;
  onGoUpcoming?: () => void;
}) {
  const firstDog = dogs?.[0] || null;
  const planIsActive = hasSubs && activeSubsCount > 0;

  // With nothing active but a paused plan on file, show that plan's contents
  // behind a frosted resume overlay
  const isPausedView = activeSubsCount === 0 && pausedSubs.length > 0;

  // Bundle subs (dog-linked plans) and legacy subs (standalone recipes, not
  // linked to a dog) are shown as separate groups.
  const { bundleItems, legacyItems } = useMemo(() => {
    const variantIndex = buildVariantIndex(products);

    const bundleCounts = new Map<string, { item: PlanItem; count: number; packSizeGrams: number | null }>();
    const legacy: PlanItem[] = [];

    const source = isPausedView ? pausedSubs : subscriptions;
    for (const sub of source) {
      if (!isPausedView && !isActiveSub(sub)) continue;

      if (isBundleSub(sub)) {
        const plan = parseBundlePlan(sub);
        for (const meal of plan.meals) {
          const count = Number(meal.count || 0);
          if (count <= 0) continue;
          const variantId = String(meal.variantId);
          const info = variantIndex.get(variantId);
          const existing = bundleCounts.get(variantId);
          if (existing) {
            existing.count += count;
          } else {
            bundleCounts.set(variantId, {
              item: {
                variantId,
                title: info?.productTitle || "Recipe",
                imageUrl: info?.imageUrl || null,
                countLabel: "",
              },
              count,
              packSizeGrams: plan.packSizeGrams,
            });
          }
        }
      } else {
        const parsed = parseLegacySubscription(sub);
        if (!parsed.variantId) continue;
        const info = variantIndex.get(parsed.variantId);
        // Treats are managed on the Extras page, not shown as plan recipes
        if (info?.tags?.includes("Treats")) continue;
        const sizeLabel = /^\d+\s*g$/i.test(parsed.variantTitle.trim())
          ? parsed.variantTitle.trim()
          : null;
        legacy.push({
          variantId: parsed.variantId,
          title: info?.productTitle || parsed.productTitle,
          imageUrl: info?.imageUrl || null,
          countLabel: sizeLabel
            ? `${parsed.quantity} x ${sizeLabel}`
            : `x ${parsed.quantity}`,
        });
      }
    }

    const bundles: PlanItem[] = [...bundleCounts.values()].map(({ item, count, packSizeGrams }) => ({
      ...item,
      countLabel: packSizeGrams ? `${count} x ${packSizeGrams}g` : `x ${count}`,
    }));

    return { bundleItems: bundles, legacyItems: legacy };
  }, [subscriptions, pausedSubs, isPausedView, products]);

  const deliveryDateLabel = formatDeliveryDate(nextDeliveryDate);
  const deliveryDays = daysUntil(nextDeliveryDate);

  // Legacy-only customers have standalone recipes with no dog-linked plan, so
  // the plan framing (title, status tiles, delivery tile) would overstate what
  // exists — show just their recipes.
  const legacyOnly = bundleItems.length === 0 && legacyItems.length > 0;

  return (
    <Box>
      {/* Greeting - centered on mobile per the Figma mobile frame */}
      <Typography
        sx={{
          fontFamily: QUIZ.font.display,
          fontSize: { xs: 41, md: 44 },
          color: QUIZ.color.ink,
          textTransform: "uppercase",
          lineHeight: 1,
          textAlign: { xs: "center", md: "left" },
        }}
      >
        Hi, {customerFirstName || "there"}
      </Typography>
      <Typography
        sx={{
          fontFamily: QUIZ.font.body,
          fontSize: 18,
          color: QUIZ.color.grey,
          mt: 1,
          mb: { xs: 3, md: 4 },
          textAlign: { xs: "center", md: "left" },
        }}
      >
        Welcome back!
      </Typography>

      {/* Two-column layout: left rail + plan card */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "232px 1fr" },
          gap: { xs: 2.5, md: 3.5 },
          alignItems: "start",
        }}
      >
        {/* Left rail — on mobile the plan card comes first (order swap below) */}
        <Stack spacing={2} sx={{ order: { xs: 2, md: 1 } }}>
          <ActionPill
            label="Manage meal plan"
            background={{ xs: QUIZ.color.brown, md: QUIZ.color.blue }}
            textColor={{ xs: QUIZ.color.cream, md: QUIZ.color.grey }}
            onClick={onGoSubscriptions}
          />
          <ActionPill
            label="Manage dogs"
            background={{ xs: QUIZ.color.blue, md: "#fff" }}
            onClick={onGoDogs}
          />
          {(dogs || []).map((dog, i) => (
            <DogProfileCard key={(dog as any).id || `${dog.name}-${i}`} dog={dog} onClick={onGoDogs} />
          ))}
        </Stack>

        {/* Plan card */}
        <Box
          sx={{
            background: "#fff",
            borderRadius: "20px",
            p: { xs: 2.5, md: 5 },
            minWidth: 0,
            order: { xs: 1, md: 2 },
          }}
        >
          <Typography
            sx={{
              fontFamily: QUIZ.font.display,
              fontSize: { xs: 41, md: 55 },
              color: QUIZ.color.ink,
              textTransform: "uppercase",
              lineHeight: 1,
              mb: 2.5,
              textAlign: { xs: "center", md: "left" },
            }}
          >
            {legacyOnly
              ? "Your Recipes"
              : dogs?.length === 1 && firstDog
              ? `${firstDog.name}’s Plan`
              : "Your Plan"}
          </Typography>

          {!legacyOnly && (
            <>
              {/* Stat tiles */}
              <Box sx={{ display: "flex", gap: 1.25, flexDirection: { xs: "column", sm: "row" }, mb: 2.5 }}>
                <StatTile
                  label="Plan status"
                  value={planIsActive ? "Active" : "Paused"}
                  background={planIsActive ? STATUS_GREEN : "#FFF0D6"}
                />
                <StatTile label="Dogs" value={String(dogs?.length || 0)} background={QUIZ.color.blue} />
              </Box>
            </>
          )}

          {/* Paused view: the frosted overlay covers only the sections below the status tiles */}
          <Box
            sx={{
              position: "relative",
              ...(isPausedView && { pointerEvents: "none", userSelect: "none" }),
            }}
          >
            {!legacyOnly && (
              <ButtonBase
                onClick={onGoUpcoming}
                sx={{
                  width: "100%",
                  background: QUIZ.color.cream2,
                  borderRadius: "8px",
                  px: 2,
                  py: 2,
                  display: "flex",
                  justifyContent: "flex-start",
                  gap: 3,
                  mb: 2.5,
                  textAlign: "left",
                  transition: "filter 150ms ease",
                  "&:hover": { filter: "brightness(0.98)" },
                }}
              >
                <LocalShippingIcon sx={{ fontSize: 56, color: QUIZ.color.brown }} />
                <Box>
                  <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: { xs: 18, md: 20 }, color: QUIZ.color.grey }}>
                    Your next delivery
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: QUIZ.font.display,
                      fontSize: { xs: 31, md: 42 },
                      color: QUIZ.color.ink,
                      textTransform: "uppercase",
                      lineHeight: 1.1,
                    }}
                  >
                    {deliveryDateLabel || "-"}
                  </Typography>
                  {deliveryDays != null && (
                    <Typography sx={{ fontFamily: QUIZ.font.label, fontSize: { xs: 18, md: 20 }, color: QUIZ.color.grey }}>
                      {deliveryDays === 0 ? "today" : `in ${deliveryDays} day${deliveryDays === 1 ? "" : "s"}`}
                    </Typography>
                  )}
                </Box>
              </ButtonBase>
            )}

          {/* Plan items */}
          <Box sx={{ background: QUIZ.color.cream, borderRadius: "8px", p: { xs: 2, md: 3.75 } }}>
            <Typography
              sx={{
                fontFamily: QUIZ.font.label,
                fontSize: { xs: 20, md: 26 },
                color: QUIZ.color.ink,
                mb: 2.5,
                lineHeight: 1,
              }}
            >
              {legacyOnly ? "Your recipes" : "Plan items"}
            </Typography>
            {bundleItems.length > 0 && (
              <Box
                sx={{
                  display: { xs: "grid", md: "flex" },
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1.25,
                  overflowX: { md: "auto" },
                  pb: 0.5,
                }}
              >
                {bundleItems.map((item, i) => (
                  <PlanItemCard
                    key={item.variantId}
                    item={item}
                    borderColor={CARD_BORDER_COLORS[i % CARD_BORDER_COLORS.length]}
                  />
                ))}
              </Box>
            )}

            {/* Legacy subscriptions: standalone recipes, not linked to a dog */}
            {legacyItems.length > 0 && (
              <>
                {bundleItems.length > 0 && (
                  <Typography
                    sx={{
                      fontFamily: QUIZ.font.label,
                      fontSize: 20,
                      color: QUIZ.color.ink,
                      mt: 3,
                      mb: 2,
                      lineHeight: 1,
                    }}
                  >
                    Individual recipes
                  </Typography>
                )}
                <Box
                  sx={{
                    display: { xs: "grid", md: "flex" },
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1.25,
                    overflowX: { md: "auto" },
                    pb: 0.5,
                  }}
                >
                  {legacyItems.map((item, i) => (
                    <PlanItemCard
                      key={item.variantId}
                      item={item}
                      borderColor={
                        CARD_BORDER_COLORS[(bundleItems.length + i) % CARD_BORDER_COLORS.length]
                      }
                    />
                  ))}
                </Box>
              </>
            )}

            {bundleItems.length === 0 && legacyItems.length === 0 && (
              <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: 16, color: QUIZ.color.grey }}>
                No meals on your plan yet.
              </Typography>
            )}
          </Box>

          {/* Frosted glass resume overlay over the paused plan */}
          {isPausedView && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 2,
                pointerEvents: "auto",
                borderRadius: "8px",
                background: "rgba(255, 253, 248, 0.55)",
                backdropFilter: "blur(14px) saturate(150%)",
                WebkitBackdropFilter: "blur(14px) saturate(150%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                px: 3,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontFamily: QUIZ.font.display,
                  fontSize: { xs: 26, md: 32 },
                  color: QUIZ.color.ink,
                  textTransform: "uppercase",
                  lineHeight: 1.1,
                }}
              >
                Your plan is paused
              </Typography>
              <Typography
                sx={{
                  fontFamily: QUIZ.font.body,
                  fontSize: { xs: 14, md: 15 },
                  fontWeight: 600,
                  color: QUIZ.color.grey,
                  maxWidth: 420,
                }}
              >
                Everything is saved just as you left it. Resume to restart your deliveries.
              </Typography>
              {onResume && (
                <QuizButton variant="primary" onClick={onResume} sx={{ mt: 1 }}>
                  Resume my plan
                </QuizButton>
              )}
            </Box>
          )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
